import { SignableDocument, SignerType } from 'app/models/document-params'
import { formatDocumentTimestamp } from './timestamp-format'

// Status shown under a document icon (C / AA / FM / AL) in the sub-trade
// documents table. Label text, colour and timestamp handling per status live
// here so the columns stay presentational.
//
// FAILED IS NOT IMPLEMENTED IN THIS ITERATION. The member below, its label and
// its red colour are kept so the spec'd status set stays complete and turning
// it on is a one-place change — but nothing derives it, so no cell can render
// it. Implementing it means: deciding what marks a document as failed (there is
// no such flag on the contract today), setting it in docIconStatusView, and
// adding the red ✕ indicator the spec pairs with it.
export enum DocIconStatus {
  Created = 'Created',
  Refreshing = 'Refreshing',
  Refreshed = 'Refreshed',
  /** NOT IMPLEMENTED this iteration — nothing sets this. */
  Failed = 'Failed',
  SignedSeller = 'SignedSeller',
  SignedBuyer = 'SignedBuyer',
  SignedBoth = 'SignedBoth',
  SentToConnect = 'SentToConnect',
  FullyExecuted = 'FullyExecuted',
}

const BLUE = '#476DB9'
const RED = '#D40054'

// Every status that can actually be derived today is blue; the red branch is
// dead until Failed is implemented.
export const docIconStatusColor = (status: DocIconStatus): string =>
  status === DocIconStatus.Failed ? RED : BLUE

// Prefix before the timestamp, e.g. 'Signed (Seller)' -> 'Signed (Seller): <ts>'.
const STATUS_PREFIX: Record<DocIconStatus, string> = {
  [DocIconStatus.Created]: 'Created',
  [DocIconStatus.Refreshing]: 'Refreshing...',
  [DocIconStatus.Refreshed]: 'Refreshed',
  // NOT IMPLEMENTED this iteration.
  [DocIconStatus.Failed]: 'Failed',
  [DocIconStatus.SignedSeller]: 'Signed (Seller)',
  [DocIconStatus.SignedBuyer]: 'Signed (Buyer)',
  [DocIconStatus.SignedBoth]: 'Signed (Both)',
  [DocIconStatus.SentToConnect]: 'Sent to Connect',
  [DocIconStatus.FullyExecuted]: 'Fully executed',
}

// The status on its own, without the timestamp — the documents table stacks the
// two (status above the icon, timestamp below) to keep the columns narrow.
export const docIconStatusPrefix = (status: DocIconStatus): string =>
  STATUS_PREFIX[status]

// Refreshing is in-flight, so it never carries a timestamp.
export const docIconStatusLabel = (
  status: DocIconStatus,
  timestamp?: string | null,
): string => {
  const prefix = STATUS_PREFIX[status]
  if (status === DocIconStatus.Refreshing || !timestamp) return prefix
  return `${prefix}: ${timestamp}`
}

export interface DocIconStatusView {
  status: DocIconStatus | null
  /** Already formatted for display — null when there is nothing to show. */
  timestamp: string | null
}

const NO_STATUS: DocIconStatusView = { status: null, timestamp: null }

interface SignatureEntry {
  signerType: SignerType
  actionTime?: string
}

// Every signature on the document, online (`signatures`) or offline via an
// admin upload (`adminSignedFor`) — the same evidence hasPartySigned accepts,
// kept in recording order so the most recent one can be identified.
const signatureEntries = (doc: SignableDocument): SignatureEntry[] => {
  const entries: SignatureEntry[] = []
  for (const signature of doc.signatures ?? []) {
    if (signature?.signerType) {
      entries.push({
        signerType: signature.signerType,
        actionTime: signature.actionTime,
      })
    }
  }
  for (const admin of doc.adminSignedFor ?? []) {
    const info = admin?.signerPartyInfo
    if (info?.signerType)
      entries.push({ signerType: info.signerType, actionTime: info.actionTime })
  }
  return entries
}

// First entry per party wins, so an online signature keeps its own time when
// the same party also has an offline record.
const signedTimeByParty = (
  entries: SignatureEntry[],
): Map<SignerType, string | undefined> => {
  const signedAt = new Map<SignerType, string | undefined>()
  for (const entry of entries) {
    if (!signedAt.has(entry.signerType))
      signedAt.set(entry.signerType, entry.actionTime)
  }
  return signedAt
}

// Who signed last: by time where the entries carry one, otherwise the last
// recorded (signatures are appended in signing order).
const lastSignature = (
  entries: SignatureEntry[],
): SignatureEntry | undefined => {
  if (entries.length === 0) return undefined
  const timed = entries.filter(entry => !!entry.actionTime)
  if (timed.length === 0) return entries[entries.length - 1]
  return timed.reduce((latest, entry) =>
    new Date(entry.actionTime as string).getTime() >=
    new Date(latest.actionTime as string).getTime()
      ? entry
      : latest,
  )
}

// Only the two trade counterparties get a signed status of their own. An Agent
// or Reviewer signature matters for completion — it clears the approval step
// and takes the document to Fully executed — never as a status by itself.
type Counterparty = Extract<SignerType, 'Seller' | 'Buyer'>

const isCounterparty = (signerType: SignerType): signerType is Counterparty =>
  signerType === 'Seller' || signerType === 'Buyer'

const SIGNED_STATUS: Record<Counterparty, DocIconStatus> = {
  Seller: DocIconStatus.SignedSeller,
  Buyer: DocIconStatus.SignedBuyer,
}

const latestOf = (times: (string | undefined)[]): string | undefined =>
  times
    .filter((t): t is string => !!t)
    .sort((a, b) => new Date(b).getTime() - new Date(a).getTime())[0]

const formatted = (iso?: string | null): string | null =>
  iso ? formatDocumentTimestamp(iso) || null : null

type SignatureStep = {
  tag: 'AllOf' | 'OneOf'
  value: { signerType: SignerType; signerParty: string }[]
}

// The Agent signs the AA offline when the deal's admin entity has no company
// set up: computeRequiredSignatures (MasterTrade.daml) then falls back to the
// agency's own parties for Reviewer/Agent instead of the company's reviewer and
// signer. Those parties never sign in the signing portal, so the packet is
// never sent to Connect — 'Signed both' is the real end state for the pair of
// counterparty signatures.
const agentSignsOffline = (
  doc: SignableDocument,
  agencyParties: (string | undefined)[],
): boolean => {
  const agency = new Set([doc.contractAdmin, ...agencyParties].filter(Boolean))
  const approvers = ((doc.requiredSignatures?.steps ?? []) as SignatureStep[])
    .flatMap(step => step?.value ?? [])
    .filter(
      signer =>
        signer?.signerType === 'Agent' || signer?.signerType === 'Reviewer',
    )
  return (
    approvers.length > 0 &&
    approvers.every(signer => agency.has(signer.signerParty))
  )
}

// Derives the status line for one document icon. Only the states with agreed
// rules are covered — Refreshing is driven by the in-flight refresh in the
// column, Failed is deliberately never returned (not implemented this
// iteration), and a state we cannot classify renders no label rather than a
// wrong one.
export const docIconStatusView = (
  doc: SignableDocument | undefined,
  // Sent to Connect / Signed both are AA-only states: the AA is the only
  // document whose required signatures continue past the two counterparties
  // (Agent/Reviewer). agencyParties are the sub-trade's own agency parties
  // (agencyAdmin / agencyManager) used to spot the offline-Agent fallback;
  // the document's contractAdmin is always included.
  {
    isAssignmentAgreement = false,
    showRefreshed = true,
    agencyParties = [],
  }: {
    isAssignmentAgreement?: boolean
    // The Funding Memo is never refreshed by hand (no refresh button on that
    // column), so it reads as Created however often the system regenerates it.
    showRefreshed?: boolean
    agencyParties?: (string | undefined)[]
  } = {},
): DocIconStatusView => {
  if (!doc) return NO_STATUS

  const entries = signatureEntries(doc)
  const signedAt = signedTimeByParty(entries)

  // Fully executed: no required signature steps remain. Strict equality on 0
  // so a missing/malformed requiredSignatures never reads as executed.
  if (doc.requiredSignatures?.steps?.length === 0) {
    return {
      status: DocIconStatus.FullyExecuted,
      timestamp: formatted(latestOf([...signedAt.values()]) ?? doc.createDate),
    }
  }

  // Both counterparties signed, approval still outstanding (AA only). An
  // offline-signing Agent never reaches Connect, so that case stops at
  // 'Signed both'. Approver progress does not change this line — it shows up
  // as Fully executed once the last approval step clears.
  if (
    isAssignmentAgreement &&
    signedAt.has('Seller') &&
    signedAt.has('Buyer')
  ) {
    return {
      status: agentSignsOffline(doc, agencyParties)
        ? DocIconStatus.SignedBoth
        : DocIconStatus.SentToConnect,
      timestamp: formatted(
        latestOf([signedAt.get('Seller'), signedAt.get('Buyer')]),
      ),
    }
  }

  // A counterparty has signed: show which one signed last, and when. Signatures
  // outrank the regeneration stamp — every change now moves refreshedAt,
  // including signing.
  const latest = lastSignature(
    entries.filter(entry => isCounterparty(entry.signerType)),
  )
  if (latest && isCounterparty(latest.signerType)) {
    return {
      status: SIGNED_STATUS[latest.signerType],
      timestamp: formatted(latest.actionTime),
    }
  }

  // Nothing signed: changed at least once since creation (refreshedAt is a
  // last-modified stamp, and with no signatures a regeneration is what moved
  // it), or still exactly as created.
  if (showRefreshed && doc.refreshedAt) {
    return {
      status: DocIconStatus.Refreshed,
      timestamp: formatted(doc.refreshedAt),
    }
  }
  return { status: DocIconStatus.Created, timestamp: formatted(doc.createDate) }
}
